import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuthStore, useFeedsStore } from '../store/useStore';
import { feeds as feedsApi } from '../lib/api';
import toast from 'react-hot-toast';

interface PopularFeed {
  id: string;
  url: string;
  title: string;
  description: string | null;
  siteUrl: string | null;
  imageUrl: string | null;
  subscriberCount: number;
}

interface StarterPackFeed {
  url: string;
  title: string;
  description: string;
}

interface StarterPack {
  id: string;
  name: string;
  description: string;
  icon: string;
  feeds: StarterPackFeed[];
}

export default function DiscoverFeeds() {
  const token = useAuthStore((state) => state.token)!;
  const setFeeds = useFeedsStore((state) => state.setFeeds);
  const [popularFeeds, setPopularFeeds] = useState<PopularFeed[]>([]);
  const [starterPacks, setStarterPacks] = useState<StarterPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [selectedFeeds, setSelectedFeeds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'popular' | 'packs'>('popular');

  useEffect(() => {
    fetchDiscoverData();
  }, []);

  async function fetchDiscoverData() {
    try {
      const [popularResponse, packsResponse] = await Promise.all([
        fetch('/api/discover/popular', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch('/api/discover/starter-packs', {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!popularResponse.ok || !packsResponse.ok) {
        throw new Error('Failed to fetch discover data');
      }

      const popularData = await popularResponse.json();
      const packsData = await packsResponse.json();

      setPopularFeeds(popularData.feeds);
      setStarterPacks(packsData.starterPacks);
    } catch (error) {
      console.error('Error fetching discover data:', error);
      toast.error('Failed to load discover data');
    } finally {
      setLoading(false);
    }
  }

  function toggleFeedSelection(feedUrl: string) {
    const newSelected = new Set(selectedFeeds);
    if (newSelected.has(feedUrl)) {
      newSelected.delete(feedUrl);
    } else {
      newSelected.add(feedUrl);
    }
    setSelectedFeeds(newSelected);
  }

  function selectAllInPack(pack: StarterPack) {
    const newSelected = new Set(selectedFeeds);
    pack.feeds.forEach(feed => newSelected.add(feed.url));
    setSelectedFeeds(newSelected);
  }

  async function handleBulkSubscribe(feedUrls: string[]) {
    if (feedUrls.length === 0) {
      toast.error('No feeds selected');
      return;
    }

    setSubscribing(true);
    try {
      const response = await fetch('/api/discover/bulk-subscribe', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ feedUrls }),
      });

      if (!response.ok) throw new Error('Failed to subscribe');

      const result = await response.json();

      if (result.summary.subscribed > 0) {
        toast.success(`Subscribed to ${result.summary.subscribed} feed${result.summary.subscribed > 1 ? 's' : ''}!`);

        // Refresh feeds list
        const updatedFeeds = await feedsApi.getAll(token);
        setFeeds(updatedFeeds);

        // Clear selections
        setSelectedFeeds(new Set());
      }

      if (result.summary.skipped > 0) {
        toast(`${result.summary.skipped} feed${result.summary.skipped > 1 ? 's were' : ' was'} already subscribed`, {
          icon: 'ℹ️',
        });
      }

      if (result.summary.errors > 0) {
        toast.error(`Failed to subscribe to ${result.summary.errors} feed${result.summary.errors > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Error subscribing to feeds:', error);
      toast.error('Failed to subscribe to feeds');
    } finally {
      setSubscribing(false);
    }
  }

  if (loading) {
    return (
      <div className="pl-4 lg:pl-6 py-10">
        <span className="kicker">Loading…</span>
      </div>
    );
  }

  // Square, hard-edged checkbox cell at the left edge of a row
  function CheckCell({ checked }: { checked: boolean }) {
    return (
      <span
        role="checkbox"
        aria-checked={checked}
        className={`inline-flex items-center justify-center w-4 h-4 border border-ink flex-shrink-0 ${
          checked ? 'bg-ink' : 'bg-canvas'
        }`}
      >
        {checked && <span className="block w-2 h-2 bg-paper" />}
      </span>
    );
  }

  function FeedRow({ url, title, description, subscriberCount }: {
    url: string;
    title: string;
    description: string | null;
    subscriberCount?: number;
  }) {
    const checked = selectedFeeds.has(url);
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => toggleFeedSelection(url)}
        onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleFeedSelection(url); } }}
        className={`grid grid-cols-[3rem_1fr] border-b border-edge-subtle cursor-pointer ${
          checked ? 'bg-surface-sunken' : 'hover:bg-surface-sunken'
        }`}
      >
        <div className="flex items-start pl-4 lg:pl-6 py-3 border-r border-edge-subtle">
          <CheckCell checked={checked} />
        </div>
        <div className="min-w-0 flex items-center gap-4 pl-4 pr-4 py-2.5">
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-ink leading-snug truncate">{title}</div>
            {description && (
              <div className="text-sm text-ink-secondary truncate">{description}</div>
            )}
          </div>
          {subscriberCount !== undefined && (
            <span className="kicker whitespace-nowrap">
              {subscriberCount} subscriber{subscriberCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    );
  }

  const selectedCount = selectedFeeds.size;

  return (
    <div className="min-h-full bg-canvas page-transition">
      {/* Header */}
      <div className="pl-4 lg:pl-6 pr-4 pt-8 pb-5">
        <div className="kicker mb-1">Discover</div>
        <h1 className="text-3xl">Feeds worth following.</h1>
        <p className="mt-2 text-ink-secondary max-w-prose">
          What's popular on this instance, or a curated starter pack to get going.
        </p>
      </div>

      {/* Tab rail */}
      <div className="sticky top-0 z-10 bg-canvas">
        <div className="rail">
          <button
            onClick={() => setActiveTab('popular')}
            className={`rail-tab ${activeTab === 'popular' ? 'is-active' : ''}`}
          >
            Popular
          </button>
          <button
            onClick={() => setActiveTab('packs')}
            className={`rail-tab ${activeTab === 'packs' ? 'is-active' : ''}`}
          >
            Starter packs
          </button>
          <span className="rail-spacer" />
          {selectedCount > 0 && (
            <>
              <span className="rail-tab is-quiet border-l border-edge">
                {selectedCount} selected
              </span>
              <button onClick={() => setSelectedFeeds(new Set())} className="rail-tab is-quiet">
                Clear
              </button>
              <button
                onClick={() => handleBulkSubscribe(Array.from(selectedFeeds))}
                disabled={subscribing}
                className="rail-tab is-active border-r-0 disabled:opacity-50"
              >
                {subscribing ? 'Subscribing…' : 'Subscribe'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Popular */}
      {activeTab === 'popular' && (
        popularFeeds.length === 0 ? (
          <div className="pl-4 lg:pl-6 pr-4 py-10 max-w-list">
            <div className="kicker mb-1">Empty</div>
            <p className="text-ink-secondary">No popular feeds yet. As people subscribe, they'll show up here.</p>
          </div>
        ) : (
          <div>
            {popularFeeds.map((feed) => (
              <FeedRow
                key={feed.id}
                url={feed.url}
                title={feed.title}
                description={feed.description}
                subscriberCount={feed.subscriberCount}
              />
            ))}
          </div>
        )
      )}

      {/* Starter packs */}
      {activeTab === 'packs' && (
        <div>
          {starterPacks.map((pack, packIndex) => (
            <section key={pack.id} className="border-b border-edge">
              <div className="flex items-end justify-between gap-4 pl-4 lg:pl-6 pr-4 pt-6 pb-3 border-b border-edge">
                <div className="min-w-0">
                  <div className="kicker mb-1">№ {String(packIndex + 1).padStart(3, '0')} · Starter pack</div>
                  <h2 className="text-xl">{pack.icon} {pack.name}</h2>
                  <p className="text-sm text-ink-secondary mt-1">{pack.description}</p>
                </div>
                <button onClick={() => selectAllInPack(pack)} className="btn">
                  Select all
                </button>
              </div>

              {pack.feeds.map((feed) => (
                <FeedRow
                  key={feed.url}
                  url={feed.url}
                  title={feed.title}
                  description={feed.description}
                />
              ))}

              <div className="flex justify-end pl-4 lg:pl-6 pr-4 py-3">
                <button
                  onClick={() => handleBulkSubscribe(pack.feeds.map(f => f.url))}
                  disabled={subscribing}
                  className="btn btn-ink"
                >
                  {subscribing ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Subscribing…</>
                  ) : (
                    `Subscribe to all ${pack.feeds.length}`
                  )}
                </button>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
